import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Sparkles,
  Building2,
  History,
  ChevronRight,
  CalendarDays,
  MapPin,
  Users,
  Package,
  Map,
} from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { compareEventsByNearestStart } from "@mvk/shared";
import { useAppStore } from "../store/useAppStore";
import { eventsService } from "../services/eventsService";
import { companiesService } from "../services/companiesService";
import { productsService } from "../services/productsService";
import { scanService } from "../services/scanService";
import type { Company, Event, Product } from "../types";
import { maxPlatform } from "../services/maxPlatform";
import { GlassCard } from "../components/GlassCard";
import { PageShell } from "../components/PageShell";
import { CompanyAvatar } from "../components/CompanyAvatar";
import { ProductCard } from "../components/ProductCard";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { applyEventPrimaryCssVar, normalizeEventHex } from "../utils/eventTheme";
import { formatCompanyLocation } from "../utils/locationLabel";
import { formatDateRange } from "../i18n";
import { EventCoverImage } from "../components/EventCoverImage";
import { WhiteLogoPlate } from "../components/WhiteLogoPlate";
import { SkeletonList } from "../components/SkeletonList";

const QUICK_ACTIONS = [
  { path: "/guide", labelKey: "home.action_guide", icon: Sparkles, color: "from-violet-500 to-indigo-500" },
  { path: "/companies", labelKey: "home.action_companies", icon: Building2, color: "from-indigo-500 to-purple-500" },
  { path: "/products", labelKey: "home.action_products", icon: Package, color: "from-amber-500 to-orange-500" },
  { path: "/schedule", labelKey: "home.action_schedule", icon: CalendarDays, color: "from-purple-500 to-pink-500" },
  { path: "/map", labelKey: "home.action_map", icon: Map, color: "from-emerald-500 to-teal-500" },
] as const;

export function HomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { selectedEventId, selectEvent, scanHistory, addScan } = useAppStore();
  const [event, setEvent] = useState<Event | undefined>();
  const [groupSiblings, setGroupSiblings] = useState<Event[]>([]);
  /** Accessible shows outside the current synergy group (e.g. Минводы while stuck on Урал). */
  const [otherEvents, setOtherEvents] = useState<Event[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  // Guide for everyone; exhibitor tech manager is only in Profile → /support.
  const quickActions = QUICK_ACTIONS;


  useEffect(() => {
    if (!selectedEventId) return;
    let cancelled = false;
    void scanService.getHistory(selectedEventId).then((scans) => {
      if (cancelled) return;
      for (const scan of scans) addScan(scan);
    });
    return () => {
      cancelled = true;
    };
  }, [addScan, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const evt = await eventsService.getById(selectedEventId);
        if (cancelled) return;
        setEvent(evt);
        const all = await eventsService.getAll().catch(() => [] as Event[]);
        if (cancelled) return;
        if (evt?.groupId) {
          setGroupSiblings(all.filter((e) => e.groupId === evt.groupId && e.id !== evt.id));
        } else {
          setGroupSiblings([]);
        }
        setOtherEvents(
          all
            .filter((e) => e.id !== evt?.id && e.groupId !== evt?.groupId)
            .filter((e) => e.status !== "past")
            .sort(compareEventsByNearestStart),
        );
        if (evt?.theme?.primaryColor) {
          applyEventPrimaryCssVar(evt.theme.primaryColor);
        }
        if (evt?.stubMode) {
          setCompanies([]);
          setFeaturedProducts([]);
          setCompanyNames({});
          return;
        }
        const [list, products] = await Promise.all([
          companiesService.list(selectedEventId, undefined, undefined, {
            limit: 3,
            enrichMap: false,
            order: "random",
          }),
          productsService.getFeatured(selectedEventId).catch(() => [] as Product[]),
        ]);
        if (!cancelled) {
          setCompanies(list);
          setFeaturedProducts(products);
          setCompanyNames(Object.fromEntries(list.map((c) => [c.id, c.name])));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId, i18n.language]);

  const eventScanCount = selectedEventId
    ? scanHistory.filter((s) => s.eventId === selectedEventId).length
    : 0;

  if (loading || !event) {
    return (
      <PageShell noPadding>
        <SkeletonList variant="home" />
      </PageShell>
    );
  }

  const primary = normalizeEventHex(event.theme?.primaryColor);
  const statusLabel =
    event.status === "active"
      ? t("home.status_active")
      : event.status === "upcoming"
        ? t("home.status_upcoming")
        : t("home.status_past");

  if (event.stubMode) {
    return (
      <PageShell noPadding>
        <div className="relative w-full max-w-full overflow-hidden">
          <div className="absolute safe-top-abs right-4 z-10">
            <LanguageSwitcher />
          </div>
          <WhiteLogoPlate className="mx-4 safe-top-mt-hero mb-2 max-h-52 rounded-2xl px-4 py-3">
            {(event.heroImage || event.logoUrl) ? (
              <EventCoverImage
                src={event.heroImage || event.logoUrl || ""}
                alt={event.title}
                imgClassName="block max-w-full w-auto h-auto max-h-44 object-contain object-center"
              />
            ) : (
              <div className="w-full min-h-36 bg-gradient-to-br from-indigo-100 to-slate-100" />
            )}
          </WhiteLogoPlate>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="relative -mt-6 mb-8 px-4"
          >
            <GlassCard variant="elevated" padding="lg">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 mb-2">
                {statusLabel}
              </span>
              <h1 className="text-[22px] text-gray-900 mb-2">{event.title}</h1>
              <div className="flex flex-wrap gap-3 text-[12px] text-gray-500 mb-4">
                <span className="flex items-center gap-1">
                  <CalendarDays size={13} style={{ color: primary }} />
                  {formatDateRange(event.startDate, event.endDate, i18n.language)}
                </span>
                {(event.venueName || event.city) && (
                  <span className="flex items-center gap-1">
                    <MapPin size={13} style={{ color: primary }} />
                    {[event.venueName, event.city].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
              {(groupSiblings.length > 0 || otherEvents.length > 0) && (
                <div className="mb-4 space-y-3">
                  {groupSiblings.length > 0 && (
                    <div>
                      <p className="text-[11px] text-gray-400 mb-2">{t("home.group_switch")}</p>
                      <div className="flex gap-2 overflow-x-auto pb-0.5" style={{ scrollbarWidth: "none" }}>
                        {groupSiblings.map((sib) => (
                          <button
                            key={sib.id}
                            type="button"
                            onClick={() => {
                              maxPlatform.hapticFeedback("light");
                              selectEvent(sib.id);
                            }}
                            className="flex-shrink-0 max-w-[11rem] rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2 text-left"
                          >
                            <span className="block text-[12px] text-indigo-800 font-medium line-clamp-2">
                              {sib.title}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {otherEvents.length > 0 && (
                    <div>
                      <p className="text-[11px] text-gray-400 mb-2">{t("home.other_events_switch")}</p>
                      <div className="flex gap-2 overflow-x-auto pb-0.5" style={{ scrollbarWidth: "none" }}>
                        {otherEvents.map((sib) => (
                          <button
                            key={sib.id}
                            type="button"
                            onClick={() => {
                              maxPlatform.hapticFeedback("light");
                              selectEvent(sib.id);
                            }}
                            className="flex-shrink-0 max-w-[11rem] rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-left"
                          >
                            <span className="block text-[12px] text-amber-900 font-medium line-clamp-2">
                              {sib.title}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-5 text-center space-y-2">
                <p className="text-[16px] text-gray-900 font-medium">{t("home.stub_title")}</p>
                <p className="text-[13px] text-gray-500 leading-relaxed">{t("home.stub_body")}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  maxPlatform.hapticFeedback("light");
                  navigate("/my");
                }}
                className="mt-4 w-full rounded-xl py-3 text-[14px] font-medium text-white"
                style={{ backgroundColor: primary }}
              >
                {t("home.stub_open_profile")}
              </button>
            </GlassCard>
          </motion.div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell noPadding>
      <div className="relative w-full max-w-full overflow-hidden">
        <div className="absolute safe-top-abs right-4 z-10">
          <LanguageSwitcher />
        </div>
        <WhiteLogoPlate className="mx-4 safe-top-mt-hero mb-2 max-h-52 rounded-2xl px-4 py-3">
          {(event.heroImage || event.logoUrl) ? (
            <EventCoverImage
              src={event.heroImage || event.logoUrl || ""}
              alt={event.title}
              imgClassName="block max-w-full w-auto h-auto max-h-44 object-contain object-center"
            />
          ) : (
            <div className="w-full min-h-36 bg-gradient-to-br from-indigo-100 to-slate-100" />
          )}
        </WhiteLogoPlate>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative -mt-6 mb-8 px-4"
        >
          <GlassCard variant="elevated" padding="lg">
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-700 mb-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1 animate-pulse" />
                  {statusLabel}
                </span>
                <h1 className="text-[22px] text-gray-900">{event.title}</h1>
              </div>
            </div>
            <p className="text-[13px] text-gray-500 mb-3 line-clamp-2">{event.description}</p>
            <div className="flex flex-wrap gap-3 text-[12px] text-gray-500">
              <span className="flex items-center gap-1">
                <CalendarDays size={13} style={{ color: primary }} />
                {formatDateRange(event.startDate, event.endDate, i18n.language)}
              </span>
              <span className="flex items-center gap-1">
                <MapPin size={13} style={{ color: primary }} />
                {event.venueName}
              </span>
              <span className="flex items-center gap-1">
                <Users size={13} style={{ color: primary }} />
                {t("home.companies_count", { n: event.companiesCount })}
              </span>
            </div>
            {(groupSiblings.length > 0 || otherEvents.length > 0) && (
              <div className="mt-4 pt-3 border-t border-gray-100 space-y-3">
                {groupSiblings.length > 0 && (
                  <div>
                    <p className="text-[11px] text-gray-400 mb-2">
                      {t("home.group_switch")}
                    </p>
                    <div className="flex gap-2 overflow-x-auto pb-0.5" style={{ scrollbarWidth: "none" }}>
                      {groupSiblings.map((sib) => (
                        <button
                          key={sib.id}
                          type="button"
                          onClick={() => {
                            maxPlatform.hapticFeedback("light");
                            selectEvent(sib.id);
                          }}
                          className="flex-shrink-0 max-w-[11rem] rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2 text-left"
                        >
                          <span className="block text-[12px] text-indigo-800 font-medium line-clamp-2">
                            {sib.title}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {otherEvents.length > 0 && (
                  <div>
                    <p className="text-[11px] text-gray-400 mb-2">
                      {t("home.other_events_switch")}
                    </p>
                    <div className="flex gap-2 overflow-x-auto pb-0.5" style={{ scrollbarWidth: "none" }}>
                      {otherEvents.map((sib) => (
                        <button
                          key={sib.id}
                          type="button"
                          onClick={() => {
                            maxPlatform.hapticFeedback("light");
                            selectEvent(sib.id);
                          }}
                          className="flex-shrink-0 max-w-[11rem] rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-left"
                        >
                          <span className="block text-[12px] text-amber-900 font-medium line-clamp-2">
                            {sib.title}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </GlassCard>
        </motion.div>
      </div>

      <div className="px-4 mt-3 space-y-5">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.35 }}
        >
          <div className="grid grid-cols-5 gap-2">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.path}
                  onClick={() => {
                    maxPlatform.hapticFeedback("light");
                    navigate(action.path);
                  }}
                  className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                >
                  <div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${action.color} flex items-center justify-center shadow-lg shadow-indigo-500/10`}
                  >
                    <Icon size={22} className="text-white" />
                  </div>
                  <span className="text-[10px] text-gray-700 font-medium text-center leading-tight">
                    {t(action.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {eventScanCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.35 }}
          >
            <GlassCard
              variant="subtle"
              padding="sm"
              className="cursor-pointer active:scale-[0.98] transition-transform"
              onClick={() => navigate("/history")}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <History size={20} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-[14px] text-gray-900">{t("home.my_scans")}</p>
                  <p className="text-[12px] text-gray-500">
                    {t("home.contacts_saved", { n: eventScanCount })}
                  </p>
                </div>
                <ChevronRight size={18} className="text-gray-300" />
              </div>
            </GlassCard>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.35 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[16px] text-gray-900 flex items-center gap-2">
              <Building2 size={18} style={{ color: primary }} />
              {t("home.section_exhibitors")}
            </h2>
            <button
              onClick={() => navigate("/companies")}
              className="text-[13px] flex items-center gap-1"
              style={{ color: primary }}
            >
              {t("home.see_all")}
              <ChevronRight size={14} />
            </button>
          </div>
          <div className="space-y-2">
            {companies.map((company) => (
              <GlassCard
                key={company.id}
                variant="subtle"
                padding="sm"
                className="cursor-pointer active:scale-[0.98] transition-transform"
                onClick={() => navigate(`/companies/${company.id}`)}
              >
                <div className="flex items-center gap-3">
                  <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-gray-900 truncate">{company.name}</p>
                    <p className="text-[12px] text-gray-500 truncate">
                      {[company.category, formatCompanyLocation(company)].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                </div>
              </GlassCard>
            ))}
          </div>
        </motion.div>

        {featuredProducts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.35 }}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[16px] text-gray-900 flex items-center gap-2">
                <Package size={18} style={{ color: primary }} />
                {t("home.section_products")}
              </h2>
              <button
                onClick={() => navigate("/products")}
                className="text-[13px] flex items-center gap-1"
                style={{ color: primary }}
              >
                {t("home.see_all")}
                <ChevronRight size={14} />
              </button>
            </div>
            <div className="space-y-2">
              {featuredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  variant="full"
                  companyName={companyNames[product.companyId]}
                />
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </PageShell>
  );
}
